/* eslint-disable react-hooks/exhaustive-deps */
import { Location } from '@eagle/data-function-types';
import { Stack } from '@ethings/ui-toolkit';
import { useTheme } from '@mui/material';
import L from 'leaflet';
import { FC, PropsWithChildren, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Popup, useMap } from 'react-leaflet';
import { useParams } from 'react-router-dom';
import { makeStyles } from '../../../theme';
import { getPanByPixels, testid } from '../../../util';

interface Props extends PropsWithChildren {
  locationData: Location;
  pane?: string;
  width?: number;
}

export interface PopupSize {
  height: number;
  width: number;
}

export const LayoutDesktop: FC<Props> = ({
  children,
  locationData,
  pane,
  width = 320,
}) => {
  const theme = useTheme();

  const useStyles = makeStyles()(() => ({
    popup: {
      '&': {
        zIndex: 2500,
      },
      '& .leaflet-popup-content': {
        fontSize: 'initial',
        minHeight: 0,
        '& p': {
          margin: 0,
        },
      },
      '& .leaflet-popup-content-wrapper': {
        borderRadius: theme.mapPopup.borderRadius,
        background: theme.mapPopup.background,
        color: theme.mapPopup.contrastText,
        padding: 0,
      },
      '& .leaflet-popup-tip-container': {
        display: 'none',
      },
    },
  }));

  const { classes } = useStyles();
  const { thingId } = useParams();
  const [popupSize, setPopupSize] = useState<PopupSize>({ height: 0, width: 0 });
  const map = useMap();
  const ref = useRef<L.Popup>(null);
  const popup = ref.current?.getElement();
  const position = useMemo(() => (new L.LatLng(locationData.latitude, locationData.longitude)), [locationData.latitude, locationData.longitude]);

  const panAroundEdge = useCallback((): void => {
    if (!map.getBounds().contains(position) || !popupSize.width || !popupSize.height) return;
    const markerPoint = map.latLngToContainerPoint(position);
    const { panXby, panYby } = getPanByPixels(markerPoint, popupSize);
    map.panBy([panXby, panYby]);
  }, [map, popupSize, position]);

  useLayoutEffect(() => {
    setPopupSize({
      height: popup?.clientHeight ?? 0,
      width: popup?.clientWidth ?? 0,
    });
  }, [popup]);

  useEffect(() => {
    panAroundEdge();
  }, [popupSize]);

  return (
    <Popup
      ref={ref}
      autoClose={false}
      autoPan={false}
      className={classes.popup}
      closeButton={false}
      minWidth={width}
      offset={[0, -25]}
      pane={pane}
      position={position}
    >
      <Stack data-testid={testid`thing-map-popup-${thingId}`} p={theme.mapPopup.padding}>
        {children}
      </Stack>
    </Popup>
  );
};
